import { AssetLoader, DeepFilterNet3Core } from 'deepfilternet3-noise-filter'
import { vclog } from '../hooks/vc-log'

/**
 * DeepFilterNet3 denoising for the microphone we publish.
 *
 * Distinct from the browser's `noiseSuppression` constraint, which is whatever
 * the platform's own filter happens to do. This runs a deep-learning denoiser
 * over the captured audio in an AudioWorklet and removes what the browser
 * filter leaves in — keyboards, chairs, a room's worth of low-level chatter.
 *
 * This shipped as RNNoise first, a much smaller GRU model. It let audible
 * disturbance through that DeepFilterNet does not, so RNNoise is gone rather
 * than kept as a fallback: a fallback that is noticeably worse than the thing
 * it stands in for produces a call where noise cancellation "sometimes works",
 * which is harder to reason about than one where it is either on or off.
 *
 * The two suppressors must never run together. Stacking them chews up quiet
 * speech and sibilants, so when this is on the browser constraint is turned off
 * (see `useLocalMedia`). One denoiser or the other, never both.
 *
 * Graph: mic -> DeepFilterNet -> make-up gain -> destination. The gain exists
 * because suppression removes signal energy: without it, turning noise
 * cancellation on audibly drops your level, which reads as "it made me quieter"
 * rather than "it removed the noise".
 *
 * One AudioContext at a time, torn down on every restart. Browsers cap
 * concurrent AudioContexts at around six, after which construction throws — and
 * a mic switch would otherwise leak one per switch.
 */

/** DeepFilterNet is trained at 48 kHz and resampling defeats the point. */
const SAMPLE_RATE = 48000

/**
 * Self-hosted assets, kept in-repo rather than pulled from a third-party CDN.
 *
 * The model is served as `.bin`, not the package's default `.tar.gz`, and that
 * detail is load-bearing: static hosts answer a `.tar.gz` with
 * `Content-Encoding: gzip`, so the browser transparently DEcompresses it before
 * `arrayBuffer()` sees it — while the WASM expects the still-gzipped bytes and
 * traps on a raw tar. The bytes are identical; only the extension differs.
 */
const DFN_ASSET_BASE = '/df3'
const DFN_WASM_URL = `${DFN_ASSET_BASE}/v2/pkg/df_bg.wasm`
const DFN_MODEL_URL = `${DFN_ASSET_BASE}/v2/models/DeepFilterNet3_onnx.bin`

/**
 * How hard DeepFilterNet may attenuate, in dB — not a percentage. The package
 * passes it straight to the model's `df_set_atten_lim`: noise is reduced by at
 * most this much (the worklet clamps it to 0-100). Higher removes more and
 * risks thin, broken-up quiet or sibilant speech; lower sounds more natural
 * and leaves more noise in.
 *
 * 35, raised from 30 on 2026-10-21: testers reported noise still audible with
 * noise cancellation on. Chosen by ear on a phone after trying 30 and 40. If
 * quiet or sibilant speech starts to sound thin or broken up, this is the
 * number to bring back down.
 */
const DFN_SUPPRESSION_LEVEL = 35

/**
 * Linear gain after denoising.
 *
 * 1.2, down from 1.4 on 2026-10-21. Measured on a phone, DeepFilterNet lowers
 * speech by only 0.3 dB, so this restores little and mostly lifts the noise
 * that is left. There is no limiter after it: at 1.4 the loudest peak of a
 * normal speaker reached 0.91 of full scale, at 1.2 it is 0.78.
 */
const MAKEUP_GAIN = 1.2

/**
 * How long to wait for a suspended AudioContext to start before giving up on
 * denoising and handing back the raw microphone.
 *
 * A context the browser is willing to run resumes in single-digit milliseconds
 * — this bound is not a performance budget, it is the escape hatch for the case
 * where the browser is never going to answer at all. Generous enough that a
 * loaded machine is not robbed of noise cancellation, short enough that nobody
 * spends it wondering why they are muted.
 */
const RESUME_TIMEOUT_MS = 2000

/**
 * A bigger audio buffer, so a slow denoiser frame does not cut the voice.
 *
 * DeepFilterNet works in 10 ms frames and runs a whole frame inside one audio
 * callback. Measured 2026-10-21 on a Nothing Phone 2: 6 ms per frame on
 * average, up to 16 ms. The default `interactive` context gave that phone a
 * 4 ms buffer, so nearly every frame ran past it and the published voice broke
 * up. 15 ms (that phone rounds it to 16) holds even its slowest frame, and
 * leaves room for slower phones, for about 12 ms more delay on the voice. If
 * cuts come back on slower phones, `'balanced'` (20 ms there) is the next step.
 */
const LATENCY_HINT: AudioContextLatencyCategory | number = 0.015

/**
 * How long to wait for the ~17 MB of DeepFilterNet assets.
 *
 * `readyCore()` was awaited unbounded, and the audio producer is created after
 * it — so a fetch that stalled (a cold cache on a bad connection, a dev server
 * hiccup, a proxy that accepts and never answers) stopped the microphone from
 * ever being published. No error, because nothing rejected; the await simply
 * never came back. This is the ceiling on that.
 */
const CORE_TIMEOUT_MS = 8000

/** Anything that counts as user activation for the autoplay policy. */
const GESTURES = ['pointerdown', 'keydown', 'touchstart'] as const

/**
 * Resolve to `null` rather than hang forever.
 *
 * The underlying promise is left running on purpose — `readyCore()` caches, so
 * a fetch that lands late still makes the NEXT attempt instant instead of
 * starting over.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise.catch(() => null),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ])
}

// The package hardcodes its asset paths; point them at the URLs above.
AssetLoader.prototype.getAssetUrls = function getAssetUrls() {
  return { wasm: DFN_WASM_URL, model: DFN_MODEL_URL }
}

/**
 * One core for the page, holding ~17 MB of fetched and compiled assets.
 *
 * Deliberately outlives any single capture: a new core per mic switch would
 * re-fetch and re-compile all of it, and `initialize()` is the slow part of
 * turning noise cancellation on.
 */
let core: DeepFilterNet3Core | null = null
let initPromise: Promise<void> | null = null

async function readyCore(): Promise<DeepFilterNet3Core> {
  if (!core) {
    core = new DeepFilterNet3Core({
      sampleRate: SAMPLE_RATE,
      noiseReductionLevel: DFN_SUPPRESSION_LEVEL,
      assetConfig: { cdnUrl: DFN_ASSET_BASE },
    })
  }
  if (!initPromise) {
    initPromise = core.initialize()
    // A failed fetch must not poison every later attempt.
    initPromise.catch(() => {
      initPromise = null
      core = null
    })
  }
  await initPromise
  return core
}

export class NoiseCancellation {
  private context: AudioContext | null = null
  /** Removes the pending gesture listeners, if a retry is armed. */
  private disarmRetry: (() => void) | null = null
  private retriesLeft = 3
  /** Detaches the statechange watcher on the live graph. */
  private unwatch: (() => void) | null = null
  private onLateReady?: (stream: MediaStream) => void
  private onSilenced?: () => void

  /** True while a graph is live, i.e. the published track is denoised. */
  get active(): boolean {
    return this.context !== null
  }

  /**
   * True while a retry is armed for the next gesture.
   *
   * Nothing is denoised yet, but this instance will hand over a stream through
   * `onLateReady` once the user touches the page — so its owner keeps it
   * rather than stopping it, because `stop()` disarms the retry.
   */
  get awaitingGesture(): boolean {
    return this.disarmRetry !== null
  }

  /**
   * Denoise `input` and return the processed stream.
   *
   * Returns `null` when DeepFilterNet cannot run — no AudioWorklet, a blocked
   * or corrupt asset, an unsupported browser. The caller publishes the raw
   * microphone in that case: a denoiser that fails should cost the denoising,
   * never the audio.
   */
  async process(
    input: MediaStreamTrack,
    onLateReady?: (stream: MediaStream) => void,
    onSilenced?: () => void,
  ): Promise<MediaStream | null> {
    await this.stop()
    // Held on the instance so the gesture retry and the statechange watcher can
    // reach them without every call site threading them through again.
    this.onLateReady = onLateReady ?? this.onLateReady
    this.onSilenced = onSilenced ?? this.onSilenced

    try {
      if (typeof AudioWorkletNode === 'undefined') {
        vclog('producer', 'noise cancellation unavailable: no AudioWorklet', {})
        return null
      }

      // Fetches and compiles on the first call only; ~17 MB, then cached by the
      // browser, so the first activation of a session is the slow one — and the
      // one that must not be allowed to hold the microphone hostage.
      const dfn = await withTimeout(readyCore(), CORE_TIMEOUT_MS)
      if (!dfn) {
        vclog('producer', 'noise cancellation assets not ready in time, publishing raw', {
          waitedMs: CORE_TIMEOUT_MS,
        })
        // Retried on the next switch or toggle, and the fetch above keeps
        // going, so the next attempt usually finds it already cached.
        return null
      }

      const context = new AudioContext({ sampleRate: SAMPLE_RATE, latencyHint: LATENCY_HINT })
      // A fresh context can start suspended, and starting it needs a user
      // gesture the page may not have had.
      //
      // The old line here was `if (suspended) await context.resume()`, on the
      // reasoning that capture happens behind a gesture so resuming is allowed.
      // That holds when the user answers a permission prompt. It does not hold
      // when the microphone permission is ALREADY granted (no prompt, so no
      // click), when a link joins the meeting on its own, or when a tab is
      // restored or reloaded in the background — and those are the ordinary
      // cases, not the exotic ones.
      //
      // Without activation the browser does not refuse. `resume()` either
      // leaves its promise pending until a gesture that may never come, or
      // resolves with the context still suspended. Awaiting it bare therefore
      // had two ways to lose the microphone and no way to report either: the
      // publish chain stalls here, before `produce()` is ever reached, or a
      // graph gets built on a suspended context and emits silence into a track
      // that looks perfectly live. Both present as "nobody can hear me" with an
      // empty console, and both clear the instant the user touches anything —
      // which is why changing the microphone appeared to fix it.
      //
      // So bound the wait, then believe `state` rather than the promise.
      if (context.state === 'suspended') {
        await Promise.race([
          context.resume().catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, RESUME_TIMEOUT_MS)),
        ])
      }
      if (context.state !== 'running') {
        // The browser will not start it yet. Publish the raw microphone NOW —
        // losing noise cancellation is bad, losing the microphone ends the
        // meeting — but do NOT settle for that being permanent: arm a retry on
        // the next gesture so denoising arrives on its own. Previously the only
        // way back was for the user to go and change their microphone, which
        // is how "changing the mic fixes it" became folklore.
        vclog('producer', 'noise cancellation deferred: audio context needs a gesture', {
          state: context.state,
          waitedMs: RESUME_TIMEOUT_MS,
        })
        await context.close().catch(() => {})
        if (this.onLateReady) this.armGestureRetry(input, this.onLateReady)
        return null
      }

      const denoiser = await dfn.createAudioWorkletNode(context)
      const source = context.createMediaStreamSource(new MediaStream([input]))
      const makeupGain = context.createGain()
      makeupGain.gain.value = MAKEUP_GAIN
      const destination = context.createMediaStreamDestination()

      source.connect(denoiser)
      denoiser.connect(makeupGain)
      makeupGain.connect(destination)

      this.context = context
      this.watchForSilence(context)
      vclog('producer', 'noise cancellation on (DeepFilterNet3)', {
        sampleRate: SAMPLE_RATE,
        suppressionLevel: DFN_SUPPRESSION_LEVEL,
        bufferMs: Math.round(context.baseLatency * 1000),
      })
      return destination.stream
    } catch (err) {
      await this.stop()
      vclog('producer', 'noise cancellation FAILED, publishing the raw microphone', {
        error: (err as Error).message,
      })
      return null
    }
  }

  /**
   * A running context can stop running, and if it does your voice becomes
   * silence with nothing to show for it.
   *
   * The graph was built once and never looked at again. But a browser suspends
   * an AudioContext for reasons that have nothing to do with us — the tab going
   * to the background, the machine sleeping, the OS default audio device
   * changing under it (plugging in headphones does exactly this), and on iOS an
   * interruption from a phone call. The microphone track stays `live`, the
   * producer stays open, RTP keeps flowing — and every packet carries silence,
   * because the destination node feeding it has stopped being fed.
   *
   * That is precisely "my video goes but my voice does not": video never
   * touches Web Audio, so it is completely unaffected and keeps working while
   * the audio half is quietly dead. And rebuilding the graph is what a
   * microphone change does, which is why that appeared to fix it.
   *
   * So: notice, try to restart, and if the browser will not restart it, tell
   * the owner to put the raw microphone back on air. Silence is the one outcome
   * that is not allowed to persist.
   */
  private watchForSilence(context: AudioContext): void {
    const onChange = (): void => {
      if (context.state === 'running') return
      if (context !== this.context) return // stale graph, already torn down
      vclog('producer', 'noise cancellation context stopped running — voice would go silent', {
        state: context.state,
      })
      void (async () => {
        await Promise.race([
          context.resume().catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, RESUME_TIMEOUT_MS)),
        ])
        if (context.state === 'running') {
          vclog('producer', 'noise cancellation context resumed itself', {})
          return
        }
        // Could not restart it. Hand the microphone back unprocessed rather
        // than keep publishing a track that carries nothing.
        vclog('producer', 'falling back to the raw microphone to stop the silence', {
          state: context.state,
        })
        this.onSilenced?.()
      })()
    }
    context.addEventListener('statechange', onChange)
    this.unwatch = () => context.removeEventListener('statechange', onChange)
  }

  /**
   * Try again on the next thing the user does.
   *
   * A meeting is full of clicks, so in practice this fires within seconds and
   * denoising switches itself on with nobody asking. Capped, so a browser that
   * refuses for some reason other than activation cannot spin on every click
   * for the length of the call.
   */
  private armGestureRetry(
    input: MediaStreamTrack,
    onLateReady: (stream: MediaStream) => void,
  ): void {
    this.disarmGestureRetry()
    if (this.retriesLeft <= 0) return
    this.retriesLeft -= 1

    const run = (): void => {
      this.disarmGestureRetry()
      void (async () => {
        // The microphone may have been switched or closed while we waited.
        if (input.readyState !== 'live') return
        const stream = await this.process(input, onLateReady)
        if (stream) onLateReady(stream)
      })()
    }

    for (const type of GESTURES) {
      document.addEventListener(type, run, { once: true, capture: true })
    }
    this.disarmRetry = () => {
      for (const type of GESTURES) {
        document.removeEventListener(type, run, { capture: true })
      }
    }
  }

  private disarmGestureRetry(): void {
    this.disarmRetry?.()
    this.disarmRetry = null
  }

  /**
   * Tear the graph down. Safe to call when nothing is running.
   *
   * The source track is deliberately not stopped: it belongs to whoever opened
   * the microphone, and stopping it here would take the mic down with the
   * effect — the exact mistake that made the camera vanish when a background
   * was applied. The core is left alive so its assets are not re-fetched.
   */
  async stop(): Promise<void> {
    // Before the early return: a denoiser being torn down must not leave a
    // listener behind that would rebuild a graph for a microphone that has
    // gone, and that can be armed while `context` is still null.
    this.disarmGestureRetry()
    this.unwatch?.()
    this.unwatch = null
    const context = this.context
    if (!context) return
    this.context = null
    try {
      await context.close()
    } catch {
      // Already closed, or closing. Nothing left to release either way.
    }
  }
}
